import { h, clear, toast, showModal, confirmModal, fold } from '../utils/dom.js';
import { iconSvg } from '../utils/icons.js';
import { Plus, Pencil, Check } from 'lucide';
import { ServiceAPI, BranchAPI, InstructorAPI } from '../services/endpoints.js';
import { validateForm, showFieldErrors, required, minLen, maxLen } from '../utils/validators.js';
import { Avatar, AVATAR_COLORS } from '../components/avatar.js';
import { Tooltip } from '../components/tooltip.js';
import { PageTabs } from '../components/ui.js';
import { SkeletonTable } from '../components/skeleton.js';

function hashCode(s) { let hash = 0; for (let i = 0; i < s.length; i++) { const ch = s.charCodeAt(i); hash = ((hash << 5) - hash) + ch; hash |= 0; } return Math.abs(hash); }

const serviceRules = {
  branch_id:         [(v) => required(String(v), 'La sucursal')],
  name:              [(v) => required(v, 'El nombre'), minLen(2, 'El nombre'), maxLen(100, 'El nombre')],
  description:       [maxLen(500, 'La descripción')],
};

const serviceEditRules = {
  ...serviceRules,
  is_active: [(v) => required(String(v), 'El estado')],
};

export const ServicesPage = async (user) => {
  const view = document.getElementById('view');
  clear(view);

  const canConfigure = user.role === 'admin';

  view.appendChild(h('div', { class: 'page-title' },
    h('h1', {}, 'Servicios'),
    canConfigure
      ? h('button', { class: 'btn', onclick: () => openCreate() }, h('span', { html: iconSvg(Plus, 16) }), 'Configurar servicio')
      : null,
  ));

  const card = h('div', { class: 'card' });
  view.appendChild(card);

  // Show skeleton while loading
  const skeleton = h('div', { class: 'table-wrap' }, h('table', { class: 'table' },
    h('thead', {}, h('tr', {},
      h('th', {}, 'Servicio'),
      h('th', {}, 'Instructor(es)'),
      h('th', {}, 'Sucursal (escuela)'),
      h('th', {}, 'Estado'),
      h('th', {}, ''),
    )),
    SkeletonTable(5, 5),
  ));
  card.appendChild(skeleton);

  let branches = [];
  let services = [];
  let instructors = [];
  let statusFilter = 'active';

  async function loadAll() {
    try {
      const requests = [ServiceAPI.list()];
      if (user.role === 'admin' || user.role === 'instructor') {
        requests.push(BranchAPI.list({ include_inactive: '0' }));
        requests.push(InstructorAPI.list({ include_inactive: '0' }));
      }
      const [svcRes, brRes, inRes] = await Promise.all(requests);
      services = svcRes.data || [];
      branches = brRes ? (brRes.data || []) : branches;
      instructors = inRes ? (inRes.data || []) : instructors;
      render();
    } catch (err) {
      toast(err.message, 'error');
    }
  }

  function render() {
    if (skeleton.parentNode) skeleton.remove();
    clear(card);

    if (!services.length) {
      card.appendChild(h('div', { class: 'table-empty' }, 'Aún no hay servicios configurados.'));
      return;
    }

    const branchFilter = user.role === 'admin'
      ? h('select', { name: 'branch_id', onchange: applyFilter },
          h('option', { value: '' }, 'Todas las sucursales'),
          ...branches.map((b) => h('option', { value: b.id }, b.name)),
        )
      : null;
    const searchQ = h('input', { type: 'search', 'aria-label': 'Buscar servicios', placeholder: 'Buscar por nombre, instructor, sucursal…', style: { minWidth: '200px' }, oninput: applyFilter });
    card.appendChild(h('div', { class: 'filter-bar' },
      h('label', { class: 'muted text-sm' }, 'Filtrar:'),
      searchQ,
      branchFilter,
      h('span', { class: 'filter-label' }, 'Estado'),
      PageTabs({
        tabs: [
          { id: 'active', label: 'Activo' },
          { id: 'inactive', label: 'Inactivo' },
        ],
        activeTab: statusFilter,
        onTabChange: (id) => { statusFilter = id; applyFilter(); },
      }),
    ));

    const tableHost = h('div', { class: 'mt-16' });
    card.appendChild(tableHost);

    function applyFilter() {
      const q = searchQ.value.toLowerCase().trim();
      const foldedQ = fold(q);
      const b = branchFilter ? branchFilter.value : '';
      const items = services.filter((svc) =>
        (!q || fold(svc.name).includes(foldedQ) || fold(svc.instructor_name || '').includes(foldedQ) || fold(svc.branch_name || '').includes(foldedQ)) &&
        (!b || Number(svc.branch_id) === Number(b)) &&
        (statusFilter === 'active' ? svc.is_active : !svc.is_active)
      );
      renderTable(tableHost, items);
    }

    applyFilter();
  }

  function renderTable(parent, items) {
    clear(parent);
    if (!items.length) {
      parent.appendChild(h('div', { class: 'table-empty' }, 'Ningún servicio coincide con el filtro.'));
      return;
    }
    parent.appendChild(h('div', { class: 'table-wrap' }, h('table', { class: 'table' },
      h('thead', {}, h('tr', {},
        h('th', {}, 'Servicio'),
        h('th', {}, 'Instructor(es)'),
        h('th', {}, 'Sucursal (escuela)'),
        h('th', {}, 'Estado'),
        h('th', {}, ''),
      )),
      h('tbody', {}, ...items.map((svc) => {
        const instructorNames = svc.instructor_names && svc.instructor_names.length
          ? svc.instructor_names
          : (svc.instructor_name ? [svc.instructor_name] : []);
        let instructorCell;
        if (instructorNames.length) {
          instructorCell = h('div', { class: 'cell-with-avatar-multi' },
            ...instructorNames.map((name) => {
              const color = AVATAR_COLORS[hashCode(name) % AVATAR_COLORS.length];
              return h('div', { class: 'cell-with-avatar', style: { marginRight: '12px' } },
                Avatar(name, color, 28),
                h('span', {}, name),
              );
            }),
          );
        } else {
          instructorCell = h('span', { class: 'muted' }, '— Sin asignar —');
        }
        return h('tr', {},
          h('td', {}, h('strong', {}, svc.name)),
          h('td', {}, instructorCell),
          h('td', {}, svc.branch_name),
          h('td', {}, h('span', { class: `badge ${svc.is_active ? 'active' : 'inactive'}` }, svc.is_active ? 'Activo' : 'Inactivo')),
          h('td', {},
            canEdit(svc)
              ? h('div', { class: 'actions-cell' },
                  Tooltip('Editar servicio', h('button', { class: 'btn-icon-only btn-edit', onclick: () => openEdit(svc) }, h('span', { html: iconSvg(Pencil, 14) }))),
                )
              : null,
          ),
        );
      }))),
    ));
  }

  function canEdit(svc) {
    if (user.role === 'admin') return Number(user.branch_id) === Number(svc.branch_id) || !user.branch_id;
    if (user.role === 'instructor') {
      return svc.instructor_ids && svc.instructor_ids.some((id) => {
        return instructors.some((i) => Number(i.id) === Number(id) && Number(i.user_id) === Number(user.id));
      });
    }
    return false;
  }

  function instructorCheckboxes(branchId, selectedIds) {
    const filtered = instructors.filter((i) => Number(i.branch_id) === Number(branchId));
    const wrap = h('div', { class: 'form-section' });
    wrap.appendChild(h('label', {}, 'Instructores'));
    if (!filtered.length) {
      wrap.appendChild(h('p', { class: 'muted text-sm' }, 'No hay instructores activos en esta sucursal.'));
      return wrap;
    }
    const selected = new Set((selectedIds || []).map(Number));
    filtered.forEach((inst) => {
      const checked = selected.has(Number(inst.id));
      const row = h('label', { class: 'checkbox-row' },
        h('input', { type: 'checkbox', name: 'instructor_ids', value: inst.id, checked: checked ? '' : null }),
        h('span', { class: 'checkbox-label' },
          Avatar(inst.full_name, AVATAR_COLORS[hashCode(inst.full_name) % AVATAR_COLORS.length], 24),
          h('span', {}, inst.full_name),
        ),
      );
      if (checked) row.querySelector('input').checked = true;
      wrap.appendChild(row);
    });
    return wrap;
  }

  function openCreate() {
    const branchSelect = h('select', { name: 'branch_id', required: true, onchange: onBranchChange },
      ...branches.filter((b) => {
        if (user.role === 'instructor') return Number(user.branch_id) === Number(b.id);
        return true;
      }).map((b) => h('option', { value: b.id }, b.name)),
    );

    const instructorContainer = h('div', { class: 'form-section' });

    function onBranchChange() {
      clear(instructorContainer);
      const bid = branchSelect.value;
      if (!bid) {
        instructorContainer.appendChild(h('p', { class: 'muted text-sm' }, 'Selecciona una sucursal para asignar instructores.'));
        return;
      }
      const list = instructorCheckboxes(bid, []);
      Array.from(list.childNodes).forEach((child) => instructorContainer.appendChild(child));
    }

    const form = h('div', { class: 'form-create' },
      h('h3', {}, 'Configurar servicio para una sucursal'),
      h('p', { class: 'muted text-sm' }, 'Crea un servicio (como una materia escolar) que se ofrecerá en una sucursal.'),
      h('div', { class: 'form-section' },
        user.role !== 'instructor' ? h('label', {}, 'Sucursal (escuela)') : null,
        user.role !== 'instructor' ? branchSelect : null,
        h('label', {}, 'Nombre del servicio'),
        h('input', { name: 'name', required: true, placeholder: 'Ej: Mentoría, Psicología…' }),
        h('textarea', { name: 'description', placeholder: '¿De qué trata este servicio?' }),
      ),
      instructorContainer,
      h('div', { class: 'btn-row mt-16' },
        h('button', { class: 'btn btn-block', onclick: submit }, h('span', { html: iconSvg(Plus, 16) }), 'Crear servicio'),
      ),
    );
    const m = showModal(form);
    form.closest('.modal')?.classList.add('modal-sheet');
    onBranchChange();

    async function submit() {
      const nameInput = form.querySelector('[name="name"]');
      const values = {
        branch_id: user.role === 'instructor' ? user.branch_id : branchSelect.value,
        name: nameInput.value.trim(),
        description: form.querySelector('[name="description"]').value.trim(),
      };
      const errors = validateForm(values, serviceRules);
      if (Object.keys(errors).length) { showFieldErrors(form, errors); return; }
      const data = {
        branch_id: Number(values.branch_id),
        name: values.name,
        description: values.description || null,
      };
      try {
        const res = await ServiceAPI.create(data);
        const created = res.data;
        const checkedBoxes = form.querySelectorAll('[name="instructor_ids"]:checked');
        if (checkedBoxes.length) {
          const ids = Array.from(checkedBoxes).map((cb) => Number(cb.value));
          await ServiceAPI.setInstructors(created.id, ids);
        }
        toast('Servicio configurado', 'success'); m.close();
        await loadAll();
      } catch (err) { toast(err.message, 'error'); }
    }
  }

  function openEdit(svc) {
    const selectedIds = svc.instructor_ids || [];
    const instructorSection = user.role === 'admin' ? instructorCheckboxes(svc.branch_id, selectedIds) : null;

    const form = h('div', { class: 'form-create' },
      h('h3', {}, `Editar ${svc.name}`),
      h('p', { class: 'muted text-sm' }, 'Modifica los datos del servicio y sus instructores.'),
      h('div', { class: 'form-section' },
        h('label', {}, 'Nombre a mostrar'),
        h('input', { name: 'name', value: svc.name || '', required: true }),
        h('label', {}, 'Descripción'),
        h('textarea', { name: 'description' }, svc.description || ''),
        h('label', {}, 'Activo'),
        h('select', { name: 'is_active', required: true },
          h('option', { value: '1' }, 'Activo'),
          h('option', { value: '0' }, 'Inactivo'),
        ),
      ),
      instructorSection,
      h('div', { class: 'btn-row mt-16' },
        h('button', { class: 'btn btn-block', onclick: submit }, h('span', { html: iconSvg(Check, 16) }), 'Guardar cambios'),
      ),
    );
    form.querySelector('[name="is_active"]').value = svc.is_active ? '1' : '0';
    const m = showModal(form);
    form.closest('.modal')?.classList.add('modal-sheet');

    async function submit() {
      const values = {
        name: form.querySelector('[name="name"]').value.trim(),
        description: form.querySelector('[name="description"]').value.trim(),
        is_active: form.querySelector('[name="is_active"]').value,
      };
      const errors = validateForm(values, serviceEditRules);
      if (Object.keys(errors).length) { showFieldErrors(form, errors); return; }
      const data = {
        name: values.name,
        description: values.description || null,
        is_active: values.is_active === '1',
      };
      if (values.is_active === '0' && svc.is_active) {
        const confirmed = await confirmModal(`¿Desactivar "${svc.name}" en ${svc.branch_name}?`, { confirmText: 'Desactivar' });
        if (!confirmed) return;
      }
      try {
        await ServiceAPI.update(svc.id, data);
        if (user.role === 'admin') {
          const checkedBoxes = form.querySelectorAll('[name="instructor_ids"]:checked');
          const ids = Array.from(checkedBoxes).map((cb) => Number(cb.value));
          await ServiceAPI.setInstructors(svc.id, ids);
        }
        toast('Actualizado', 'success'); m.close(); loadAll();
      } catch (err) { toast(err.message, 'error'); }
    }
  }

  await loadAll();
};
